// Approvals.tsx — maker-checker workflow with two-pane layout + diff viewer.
// Loads live approvals from the API and refreshes after every transition.

import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "@/components/primitives/Toast";
import { api } from "@/lib/api";
import { useBadgeStore } from "@/store/badges";
import type { ApprovalRequest } from "@/lib/types";

type Status = "pending" | "approved" | "rejected" | "withdrawn" | "expired";
type Priority = "low" | "med" | "high" | "critical";
type Filter = "mine" | "to-approve" | "all";

interface ChangeRow {
  id: string;
  recordId: string;
  recordName: string;
  domain: string;
  maker: string;
  reviewer?: string;
  submittedAt: string;
  hoursOpen: number;
  priority: Priority;
  status: Status;
  summary: string;
  impactCount: number;
  diff: Array<{ key: string; before: string; after: string }>;
}

function hoursBetween(iso: string): number {
  const t = new Date(iso).getTime();
  if (isNaN(t)) return 0;
  return Math.max(0, Math.round((Date.now() - t) / 3_600_000));
}

function approvalToRow(a: ApprovalRequest): ChangeRow {
  return {
    id: a.id,
    recordId: a.recordId,
    recordName: a.recordName ?? a.recordId,
    domain: a.domainId,
    maker: a.maker,
    reviewer: a.checker,
    submittedAt: a.makerAt,
    hoursOpen: a.status === "pending" ? hoursBetween(a.makerAt) : 0,
    priority: a.priority,
    status: a.status,
    summary: a.changeSummary ?? "",
    impactCount: a.impactCount ?? 0,
    diff: (a.diff ?? []).map(d => ({ key: d.key, before: String(d.before ?? "—"), after: String(d.after ?? "—") })),
  };
}

const SLA_HOURS = 24;

const PRIORITY_TONE: Record<Priority, { bg: string; fg: string }> = {
  low:      { bg: "var(--md-sc-low)",               fg: "var(--md-on-surface-var)" },
  med:      { bg: "var(--md-tertiary-container)",   fg: "var(--md-on-tertiary-container)" },
  high:     { bg: "var(--md-primary-container)",    fg: "var(--md-on-primary-container)" },
  critical: { bg: "var(--md-error-container)",      fg: "var(--md-on-error-container)" },
};

const STATUS_TONE: Record<Status, { bg: string; fg: string }> = {
  pending:   { bg: "var(--md-tertiary-container)", fg: "var(--md-on-tertiary-container)" },
  approved:  { bg: "var(--md-secondary-container)", fg: "var(--md-on-secondary-container)" },
  rejected:  { bg: "var(--md-error-container)",     fg: "var(--md-on-error-container)" },
  withdrawn: { bg: "var(--md-sc-low)",               fg: "var(--md-on-surface-var)" },
  expired:   { bg: "var(--md-sc-low)",               fg: "var(--md-on-surface-var)" },
};

// @ts-expect-error retained for reference; not invoked in production
function _unusedMockChanges(): ChangeRow[] {
  return [
    {
      id: "CR-2841", recordId: "AAPL", recordName: "Apple Inc.", domain: "instruments",
      maker: "Olivia Park", reviewer: "Marcus Chen",
      submittedAt: "2025-05-15T09:14:00Z", hoursOpen: 3,
      priority: "med", status: "pending",
      summary: "Refresh shares outstanding after Q2 10-Q filing",
      impactCount: 14,
      diff: [
        { key: "shares_out", before: "15280000000", after: "15334000000" },
        { key: "market_cap_usd", before: "2843000000000", after: "2853000000000" },
        { key: "last_filing", before: "2025-02-12", after: "2025-05-12" },
      ],
    },
    {
      id: "CR-2842", recordId: "EURUSD", recordName: "EUR/USD spot", domain: "fx-pairs",
      maker: "Priya Shankar",
      submittedAt: "2025-05-14T19:00:00Z", hoursOpen: 18,
      priority: "high", status: "pending",
      summary: "Update settlement convention from T+2 to T+1",
      impactCount: 184,
      diff: [
        { key: "settlement_days", before: "2", after: "1" },
        { key: "effective_from", before: "2024-09-01", after: "2025-05-28" },
      ],
    },
    {
      id: "CR-2843", recordId: "TSLA", recordName: "Tesla, Inc.", domain: "instruments",
      maker: "Marcus Chen", reviewer: "Olivia Park",
      submittedAt: "2025-05-13T11:00:00Z", hoursOpen: 50,
      priority: "critical", status: "pending",
      summary: "Re-classify industry from Automotive to Diversified Technology",
      impactCount: 92,
      diff: [
        { key: "industry", before: "auto-mfg", after: "tech-diversified" },
        { key: "sector",   before: "Consumer Discretionary", after: "Information Technology" },
      ],
    },
    {
      id: "CR-2840", recordId: "JPY", recordName: "Japanese Yen", domain: "currencies",
      maker: "Olivia Park", reviewer: "Priya Shankar",
      submittedAt: "2025-05-12T08:30:00Z", hoursOpen: 0,
      priority: "low", status: "approved",
      summary: "Add ISO-4217 alpha-3 alternate code",
      impactCount: 0,
      diff: [{ key: "iso_alt", before: "—", after: "JPY-ALT-01" }],
    },
    {
      id: "CR-2839", recordId: "BTC", recordName: "Bitcoin", domain: "instruments",
      maker: "Marcus Chen", submittedAt: "2025-05-11T16:00:00Z", hoursOpen: 0,
      priority: "high", status: "rejected",
      summary: "Reclassify as security",
      impactCount: 47,
      diff: [{ key: "instrument_class", before: "crypto", after: "security" }],
    },
  ];
}

export function Approvals() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [rows, setRows] = useState<ChangeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>("all");
  const [selected, setSelected] = useState(new Set<string>());
  const [selectedId, setSelectedId] = useState<string | undefined>(requestId);
  const [decisionComment, setDecisionComment] = useState("");

  const refreshBadge = useBadgeStore(s => s.refreshApprovalsCount);
  const refresh = async () => {
    try {
      const res = await api.approvals.list({ pageSize: 200 });
      const items = (res.items ?? []).map(approvalToRow);
      setRows(items);
      refreshBadge();
      return items;
    } catch (e) {
      toast(e instanceof Error ? `Load failed: ${e.message}` : "Load failed", "error");
      return [];
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    refresh().then(items => {
      if (cancelled) return;
      if (!selectedId && items.length) setSelectedId(items[0].id);
      setLoading(false);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (requestId) setSelectedId(requestId);
  }, [requestId]);

  const onApprove = async (id: string) => {
    try {
      await api.approvals.approve(id, decisionComment || undefined);
      setDecisionComment("");
      await refresh();
      toast(`Approved ${id}`, "ok");
    } catch (e) {
      toast(e instanceof Error ? `Approve failed: ${e.message}` : "Approve failed", "error");
    }
  };
  const onReject = async (id: string) => {
    if (!decisionComment.trim()) { toast("Comment required to reject", "warn"); return; }
    try {
      await api.approvals.reject(id, decisionComment);
      setDecisionComment("");
      await refresh();
      toast(`Rejected ${id}`, "warn");
    } catch (e) {
      toast(e instanceof Error ? `Reject failed: ${e.message}` : "Reject failed", "error");
    }
  };
  const onBulkApprove = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    try {
      const res = await api.approvals.bulkApprove(ids);
      await refresh();
      toast(`Bulk approved ${res.ok}/${ids.length}`, res.fail ? "warn" : "ok");
      setSelected(new Set());
    } catch (e) {
      toast(e instanceof Error ? `Bulk approve failed: ${e.message}` : "Bulk approve failed", "error");
    }
  };

  const me = "Olivia Chen";
  const filtered = rows.filter(r => {
    if (filter === "mine")        return r.maker === me;
    if (filter === "to-approve")  return r.status === "pending" && r.maker !== me;
    return true;
  });

  const current = rows.find(r => r.id === selectedId) ?? filtered[0];
  const canApproveCurrent = !!current && current.status === "pending" && current.maker !== me;

  function toggleSelect(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  }

  function pickRow(id: string) {
    setSelectedId(id);
    navigate(`/approvals/${id}`, { replace: true });
  }

  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "minmax(360px, 1fr) minmax(380px, 1.2fr)",
      gap: 0,
      height: "calc(100vh - 60px)",
      background: "var(--md-bg)",
    }}>
      {/* LEFT: Queue */}
      <div style={{ display: "flex", flexDirection: "column", borderRight: "1px solid var(--md-outline-var)", minWidth: 0 }}>
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid var(--md-outline-var)" }}>
          <div className="font-mono" style={eyebrowStyle()}>MAKER-CHECKER · CHANGE REQUESTS</div>
          <h1 className="font-display" style={{ margin: "6px 0 16px", font: "var(--t-headline-md)", letterSpacing: "-0.02em" }}>
            <span style={{ fontStyle: "italic", fontWeight: 400 }}>{loading ? "…" : filtered.filter(f => f.status === "pending").length}</span> awaiting review
          </h1>
          <div role="tablist" aria-label="Filter approvals" style={{ display: "flex", gap: 8 }}>
            {([
              ["mine", "My requests"], ["to-approve", "Awaiting my approval"], ["all", "All"],
            ] as const).map(([k, label]) => (
              <button key={k} role="tab" aria-selected={filter === k}
                onClick={() => setFilter(k)} style={chipBtn(filter === k)}>{label}</button>
            ))}
          </div>
        </div>

        <div className="scroll" style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
          {filtered.length === 0 && (
            <div style={{ padding: 40, textAlign: "center", color: "var(--md-on-surface-var)" }}>
              <div style={{ fontSize: 40 }} aria-hidden>🪐</div>
              <div style={{ font: "var(--t-title-md)", marginTop: 8 }}>Nothing here</div>
              <div style={{ font: "var(--t-body-md)", marginTop: 4 }}>No change requests match this filter.</div>
            </div>
          )}
          {filtered.map(r => (
            <div
              key={r.id}
              className="row-hover"
              style={{
                background: r.id === selectedId ? "var(--md-secondary-container)" : "transparent",
                color: r.id === selectedId ? "var(--md-on-secondary-container)" : "var(--md-on-surface)",
                padding: "14px 20px",
                borderBottom: "1px solid var(--md-outline-var)",
                display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 12, alignItems: "start",
                transition: "background var(--d-short-2)",
              }}
            >
              <input
                type="checkbox" checked={selected.has(r.id)}
                onChange={() => toggleSelect(r.id)}
                aria-label={`Select ${r.id}`}
                style={{ marginTop: 4 }}
              />
              <button
                onClick={() => pickRow(r.id)}
                aria-current={r.id === selectedId}
                style={{
                  appearance: "none", border: "none", background: "transparent",
                  textAlign: "left", padding: 0, color: "inherit", cursor: "pointer",
                  minWidth: 0,
                }}
              >
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
                  <span className="font-mono" style={{ font: "var(--t-mono-sm)", color: "var(--md-on-surface-var)" }}>{r.id}</span>
                  <Pill toneObj={PRIORITY_TONE[r.priority]}>{r.priority}</Pill>
                  <Pill toneObj={STATUS_TONE[r.status]}>{r.status}</Pill>
                </div>
                <div style={{ font: "var(--t-title-md)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.recordName}
                </div>
                <div style={{ font: "var(--t-body-sm)", color: "var(--md-on-surface-var)", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                  {r.summary}
                </div>
                <div style={{ display: "flex", gap: 10, marginTop: 8, font: "var(--t-body-sm)", color: "var(--md-on-surface-var)" }}>
                  <span>by {r.maker}</span>
                  <span>· {r.impactCount} affected</span>
                </div>
              </button>
              <SlaTimer hours={r.hoursOpen} done={r.status !== "pending"} />
            </div>
          ))}
        </div>

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <div style={{
            padding: "12px 20px",
            borderTop: "1px solid var(--md-outline-var)",
            background: "var(--md-sc-low)",
            display: "flex", alignItems: "center", gap: 12,
            animation: "slideUp var(--d-med-1) var(--ease-spring-soft)",
          }}>
            <span style={{ font: "var(--t-label-lg)" }}>{selected.size} selected</span>
            <button style={btnFilled()} onClick={onBulkApprove}>Bulk approve</button>
            <button style={btnOutlined()} onClick={() => setSelected(new Set())}>Clear</button>
            <div style={{ marginLeft: "auto", font: "var(--t-body-sm)", color: "var(--md-on-surface-var)" }}>
              Dual-control: you cannot approve your own changes.
            </div>
          </div>
        )}
      </div>

      {/* RIGHT: Detail */}
      <div className="scroll" style={{ overflowY: "auto", padding: "28px 32px", minWidth: 0 }}>
        {current ? (
          <DetailPane row={current} canApprove={!!canApproveCurrent} comment={decisionComment} setComment={setDecisionComment} onApprove={() => onApprove(current.id)} onReject={() => onReject(current.id)} />
        ) : (
          <div style={{ padding: 40, textAlign: "center", color: "var(--md-on-surface-var)" }}>
            Select a change request to review.
          </div>
        )}
      </div>
      <style>{`@keyframes slideUp { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
    </div>
  );
}

function DetailPane({ row, canApprove, comment, setComment, onApprove, onReject }: {
  row: ChangeRow; canApprove: boolean; comment: string; setComment: (v: string) => void;
  onApprove: () => void; onReject: () => void;
}) {
  return (
    <>
      <div className="font-mono" style={eyebrowStyle()}>{row.domain.toUpperCase()} · {row.id}</div>
      <h2 className="font-display" style={{
        margin: "8px 0 14px", font: "var(--t-headline-lg)", letterSpacing: "-0.02em",
        fontStyle: "italic", fontWeight: 400,
      }}>{row.recordName}</h2>
      <p style={{ font: "var(--t-body-lg)", margin: "0 0 18px", color: "var(--md-on-surface)" }}>{row.summary}</p>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 24 }}>
        <Pill toneObj={PRIORITY_TONE[row.priority]}>Priority: {row.priority}</Pill>
        <Pill toneObj={STATUS_TONE[row.status]}>{row.status}</Pill>
        <Pill toneObj={{ bg: "var(--md-sc-low)", fg: "var(--md-on-surface-var)" }}>
          <span className="font-mono">{row.impactCount}</span>&nbsp;downstream records
        </Pill>
        <Pill toneObj={{ bg: "var(--md-sc-low)", fg: "var(--md-on-surface-var)" }}>
          Maker: {row.maker}
        </Pill>
        {row.reviewer && (
          <Pill toneObj={{ bg: "var(--md-sc-low)", fg: "var(--md-on-surface-var)" }}>Reviewer: {row.reviewer}</Pill>
        )}
      </div>

      {/* Diff viewer */}
      <section aria-label="Field changes" style={{
        border: "1px solid var(--md-outline-var)", borderRadius: "var(--r-lg)",
        background: "var(--md-sc-lowest)", overflow: "hidden",
      }}>
        <div style={{ display: "grid", gridTemplateColumns: "200px 1fr 1fr", padding: "12px 20px", background: "var(--md-sc-low)",
          font: "var(--t-label-md)", color: "var(--md-on-surface-var)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
          <div>Field</div><div>Before</div><div>After</div>
        </div>
        {row.diff.map((d, i) => (
          <div key={d.key} role="group" aria-label={`Change for ${d.key}`} style={{
            display: "grid", gridTemplateColumns: "200px 1fr 1fr",
            padding: "14px 20px", gap: 16,
            borderTop: i > 0 ? "1px solid var(--md-outline-var)" : "none",
            alignItems: "center",
          }}>
            <div className="font-mono" style={{ font: "var(--t-mono-md)", color: "var(--md-on-surface-var)" }}>{d.key}</div>
            <div className="font-mono" style={{
              font: "var(--t-mono-md)",
              background: "var(--md-error-container)", color: "var(--md-on-error-container)",
              padding: "8px 12px", borderRadius: "var(--r-sm)",
              textDecoration: "line-through", textDecorationColor: "rgba(0,0,0,.25)",
            }}>{d.before}</div>
            <div className="font-mono" style={{
              font: "var(--t-mono-md)",
              background: "var(--md-secondary-container)", color: "var(--md-on-secondary-container)",
              padding: "8px 12px", borderRadius: "var(--r-sm)",
            }}>{d.after}</div>
          </div>
        ))}
      </section>

      {/* Decision */}
      {row.status === "pending" && (
        <section style={{ marginTop: 24 }}>
          <div className="font-mono" style={eyebrowStyle()}>Decision</div>
          <label htmlFor="dec-comment" style={{ font: "var(--t-label-lg)", display: "block", margin: "12px 0 6px" }}>
            Comment {canApprove ? <span style={{ color: "var(--md-on-surface-var)", font: "var(--t-body-sm)" }}>(required for reject)</span> : null}
          </label>
          <textarea
            id="dec-comment" value={comment} onChange={e => setComment(e.target.value)}
            placeholder={canApprove ? "Explain your decision…" : "You cannot approve your own change. Add a comment for context."}
            style={{
              width: "100%", boxSizing: "border-box", minHeight: 88, resize: "vertical",
              background: "var(--md-sc-low)", border: "1px solid var(--md-outline-var)",
              borderRadius: "var(--r-md)", padding: "10px 14px", font: "var(--t-body-md)",
              color: "var(--md-on-surface)", outline: "none",
            }}
          />
          {!canApprove && (
            <div role="note" style={{
              marginTop: 12, padding: "10px 14px",
              borderRadius: "var(--r-md)", background: "var(--md-tertiary-container)",
              color: "var(--md-on-tertiary-container)", font: "var(--t-body-sm)",
            }}>
              Dual-control enforced: a different reviewer must approve this change.
            </div>
          )}
          <div style={{ display: "flex", gap: 10, marginTop: 16, justifyContent: "flex-end" }}>
            <button style={{ ...btnOutlined(), color: "var(--danger)", borderColor: "var(--danger)" }}
              disabled={!comment.trim()} onClick={onReject}>Reject</button>
            <button style={btnFilled()} disabled={!canApprove} onClick={onApprove}>Approve</button>
          </div>
        </section>
      )}
    </>
  );
}

function Pill({ children, toneObj }: { children: ReactNode; toneObj: { bg: string; fg: string } }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "4px 12px", borderRadius: "var(--r-pill)",
      background: toneObj.bg, color: toneObj.fg,
      font: "var(--t-label-md)", lineHeight: 1.4, whiteSpace: "nowrap",
      textTransform: "capitalize",
    }}>{children}</span>
  );
}

function SlaTimer({ hours, done }: { hours: number; done: boolean }) {
  if (done) return <span className="font-mono" style={{ font: "var(--t-mono-sm)", color: "var(--md-on-surface-var)" }}>—</span>;
  const overdue = hours > SLA_HOURS;
  const color = overdue ? "var(--danger)" : hours > SLA_HOURS * 0.75 ? "var(--warning)" : "var(--md-on-surface-var)";
  return (
    <span className="font-mono tabular" title={overdue ? "Past SLA" : "Within SLA"} style={{
      font: "var(--t-mono-sm)", color,
      padding: "4px 10px", borderRadius: "var(--r-pill)",
      background: overdue ? "var(--md-error-container)" : "transparent",
      whiteSpace: "nowrap", alignSelf: "start",
    }}>{hours}h</span>
  );
}

function eyebrowStyle(): CSSProperties {
  return {
    font: "var(--t-mono-sm)", color: "var(--md-tertiary)",
    textTransform: "uppercase", letterSpacing: "0.12em",
  };
}
function btnFilled(): CSSProperties {
  return {
    appearance: "none", border: "none", cursor: "pointer",
    background: "var(--md-primary)", color: "var(--md-on-primary)",
    font: "var(--t-label-lg)", height: 40, padding: "0 20px",
    borderRadius: "var(--r-pill)",
  };
}
function btnOutlined(): CSSProperties {
  return {
    appearance: "none", cursor: "pointer",
    background: "transparent", color: "var(--md-on-surface)",
    border: "1px solid var(--md-outline)",
    font: "var(--t-label-lg)", height: 40, padding: "0 20px",
    borderRadius: "var(--r-pill)",
  };
}
function chipBtn(active: boolean): CSSProperties {
  return {
    appearance: "none", cursor: "pointer", border: "1px solid var(--md-outline-var)",
    background: active ? "var(--md-secondary-container)" : "var(--md-sc-lowest)",
    color: active ? "var(--md-on-secondary-container)" : "var(--md-on-surface-var)",
    padding: "6px 14px", borderRadius: "var(--r-pill)", font: "var(--t-label-md)",
  };
}

export default Approvals;
